import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SignaturePad from 'signature_pad';
import { Visita } from '../../consts/visitasTypes';
import {
    useGetTiposVisitasQuery,
    useGetMotivosFallaVisitaQuery,
    useGetVisitaByIdQuery,
    useLazyGetVisitaSiniestroQuery,
    usePatchEjecutionVisitMutation
} from '../../../../services/visitasApi';
import { DatosSiniestroApi } from '../../consts/types';
import { useSnackbar } from 'notistack';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../../constants/values';
import { selectCurrentUser } from '../../../../features/auth/authSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks/reduxHooks';
import { useTranslation } from 'react-i18next';
import { useLazyGetPresentacionesByEstablecimientoQuery } from '../../../../services/presentacionesApi';
import { useUpdatePresentationStateMutation } from '../../../../services/presentacionesApi';
import { useGetEmpresaQuery } from '../../../../services/companyApi';
import { useGetUserFirmaQuery } from '../../../../services/usersApi';
import { setEstablishmentId } from '../../../../redux/slices/establishmentSlice';
import { ESTADOS_VISITA_CONCLUIDOS, VisitaEstado } from '../../consts/visitasStatus';
import { getMotivoFallaDestino } from '../../consts/motivoFallaDestino';
import { calcularTotalPersonal } from '../../utils/dotacion';
import { useRoles } from '../../../../hooks/useRoles';
import {
    FacsimileOrganismoKey,
    getFacsimileOrganismoKey,
    getResponsableFacsimile,
    RESPONSABLE_FACSIMILES_BY_ORGANISMO
} from '../../consts/organismoFacsimileSignature';
import {
    buildResponsableFacsimileImage,
    isSignatureUrl,
    signatureUrlToBase64
} from '../../utils/facsimileSignature';
interface TipoActividad {
    id: number;
    descripcion: string;
    tiposArchivo: { id: number; descripcion: string }[];
}

const TECHNICAL_VISIT_TYPE_ID = 466;
const SEGUIMIENTO_VEROSIMILITUD_TYPE_ID = 473;
const FORM_DATA_STORAGE_KEY = 'formDataEjecutionsVisits';
const normalizeActivityDescription = (description?: string | null) =>
    (description ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toUpperCase();
// El borrador se guarda para poder volver a el (p.ej. al volver de "Seguimiento
// Verosimilitud"/RGRL) sin perder lo tipeado. Antes se guardaba bajo una clave global fija:
// al cambiar de visita, o de usuario en la misma pestana, se restauraba el borrador de
// otra persona/visita (incluida su firma ya dibujada). Se scopea por visita + usuario para
// que cada preventor tenga su propio borrador por visita.
const getFormDataStorageKey = (idVisita: unknown, idUsuario: unknown) =>
    `${FORM_DATA_STORAGE_KEY}_${idVisita ?? 'sin-visita'}_${idUsuario ?? 'sin-usuario'}`;

// Se llama al entrar al listado de visitas, para no dejar arrastrando el borrador de una
// ejecucion abandonada (de esta visita+usuario o de cualquier otra) hacia la proxima vez que
// se abra esa misma visita.
export const clearAllExecutionVisitDrafts = () => {
    Object.keys(localStorage)
        .filter((key) => key.startsWith(`${FORM_DATA_STORAGE_KEY}_`))
        .forEach((key) => localStorage.removeItem(key));
};

const getStoredFormData = (storageKey: string) => {
    const saved = localStorage.getItem(storageKey);

    if (!saved) return null;

    try {
        return JSON.parse(saved);
    } catch {
        localStorage.removeItem(storageKey);
        return null;
    }
};

const normalizeNumber = (value: number | null | undefined) => (value === 0 ? null : (value ?? null));

const normalizeBoolean = (value: boolean | null | undefined) => value ?? null;

// Estados en los que la visita todavia esta a cargo del preventor para completar/firmar (no
// esta cerrada ni en revision), momento en el que corresponde precargar su firma de perfil.
const PREVENTOR_SIGNATURE_PREFILL_STATES = [VisitaEstado.PROGRAMADA, VisitaEstado.EN_PROCESO];

// El CUIL tiene formato XX-DDDDDDDD-X; el DNI es el bloque de 8 digitos del medio.
const getDniFromCuil = (cuil?: string | null): string => {
    const digits = (cuil ?? '').replace(/\D/g, '');
    return digits.length === 11 ? digits.slice(2, 10) : '';
};
const INTERVIEWED_FORM_FIELDS = [
    'nombreEntrevistado',
    'dniEntrevistado',
    'caracterEntrevistado',
    'telefonoEntrevistado',
    'emailEntrevistado'
];

type OpenVerosimilitudParams = {
    mode: 'create' | 'edit';
    idVerosimilitud?: number;
};

export const useEjecutionsVisits = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { isAdminBO, isPreventor } = useRoles();
    const currentUser = useAppSelector(selectCurrentUser);
    // El firmaUrl del usuario logueado (Redux) es una URL presignada que puede haber vencido
    // desde el login; para precargar la firma en la ejecucion se pide una fresca aca, igual que
    // hace el perfil.
    const { data: firmaUsuarioData } = useGetUserFirmaQuery(undefined, { skip: !isPreventor });
    const [isFormInitialized, setIsFormInitialized] = useState(false);

    const { t } = useTranslation('visitas');
    const [rgrlPresentaciones, setRgrlPresentaciones] = useState<any[]>([]);
    const idVisita = location.state?.selectedVisita?.id || {};
    const formDataStorageKey = getFormDataStorageKey(idVisita, currentUser?.id);

    const {
        data: refreshedVisita,
        isLoading: loadingVerosimilitud,
        refetch: refetchVisita
    } = useGetVisitaByIdQuery({
        idVisita
    });

    const selectedVisita = refreshedVisita?.data || location.state?.selectedVisita;
    const isFailedVisitByState =
        selectedVisita?.estado === VisitaEstado.FALLIDA || selectedVisita?.estado === 3;

    // El shape de establecimiento.organismo puede variar entre la data de navegacion
    // (location.state) y la data del detalle (useGetVisitaByIdQuery), llegando en momentos
    // distintos. Una vez detectado un organismo con firma facsimil (CPE, Tribunal), la clave
    // queda "trabada" para evitar que la UI alterne entre el facsimil y el canvas de firma.
    const [facsimileOrganismoKey, setFacsimileOrganismoKey] = useState<FacsimileOrganismoKey | null>(
        () => getFacsimileOrganismoKey(location.state?.selectedVisita?.establecimiento?.organismo?.id)
    );
    const detectedFacsimileOrganismoKey =
        getFacsimileOrganismoKey(selectedVisita?.establecimiento?.organismo?.id) ||
        getFacsimileOrganismoKey(location.state?.selectedVisita?.establecimiento?.organismo?.id);

    useEffect(() => {
        if (detectedFacsimileOrganismoKey && !facsimileOrganismoKey) {
            setFacsimileOrganismoKey(detectedFacsimileOrganismoKey);
        }
    }, [detectedFacsimileOrganismoKey, facsimileOrganismoKey]);

    const hasFacsimileOrganismo = Boolean(facsimileOrganismoKey);
    const activeResponsableFacsimile = facsimileOrganismoKey
        ? RESPONSABLE_FACSIMILES_BY_ORGANISMO[facsimileOrganismoKey]
        : null;
    // Los datos de "la persona entrevistada/del establecimiento" solo se autocompletan para
    // CPE. Para Tribunal unicamente se precarga la firma facsimil del Responsable (arriba);
    // el Entrevistado se completa siempre a mano.
    const activeEntrevistadoFacsimile =
        facsimileOrganismoKey === 'cpe' ? activeResponsableFacsimile : null;

    const SRT_EDITABLE_STATES = [
        VisitaEstado.PENDIENTE,
        VisitaEstado.PROGRAMADA,
        VisitaEstado.EN_PROCESO
    ];
    const readOnlySrt = !SRT_EDITABLE_STATES.includes(selectedVisita?.estado);
    const esVisitaInvestigacion = Array.isArray(selectedVisita?.actividades)
        ? selectedVisita.actividades.some((actividad: any) =>
              normalizeActivityDescription(actividad?.tipoVisita?.descripcion).includes(
                  'INVESTIGACION'
              )
          )
        : false;

    const esSeguimientoVerosimilitud = Array.isArray(selectedVisita?.actividades)
        ? selectedVisita.actividades.some(
              (actividad: any) => actividad?.tipoVisita?.id === SEGUIMIENTO_VEROSIMILITUD_TYPE_ID
          )
        : false;

    const PREVENTOR_EDITS_STATES = ESTADOS_VISITA_CONCLUIDOS;

    const getPrefilledObservationLabel = useCallback((visita?: any) => {
        const actividades = Array.isArray(visita?.actividades) ? visita.actividades : [];

        const hasSeguimientoVerosimilitud = actividades.some(
            (actividad: any) => actividad?.tipoVisita?.id === SEGUIMIENTO_VEROSIMILITUD_TYPE_ID
        );
        const hasTechnicalVisit = actividades.some(
            (actividad: any) =>
                actividad?.tipoVisita?.id === TECHNICAL_VISIT_TYPE_ID ||
                normalizeActivityDescription(actividad?.tipoVisita?.descripcion).includes('TECNICA')
        );
        const hasVerosimilitud = actividades.some(
            (actividad: any) =>
                normalizeActivityDescription(actividad?.tipoVisita?.descripcion) ===
                'VEROSIMILITUD'
        );
        const hasRgrl = actividades.some(
            (actividad: any) =>
                normalizeActivityDescription(actividad?.tipoVisita?.descripcion) === 'RGRL'
        );

        // El seguimiento de verosimilitud arranca sin observación: la completa el preventor.
        if (hasSeguimientoVerosimilitud) {
            return '';
        }

        if (hasRgrl && hasVerosimilitud) {
            return t('execution.detail.prefilledObservations.rgrlAndVerosimilitud');
        }

        if (hasTechnicalVisit) {
            return t('execution.detail.prefilledObservations.technicalVisit');
        }

        if (hasVerosimilitud) {
            return t('execution.detail.prefilledObservations.verosimilitud');
        }

        return '';
    }, [t]);

    const mapVisitaToFormData = useCallback((visita: any) => {
        const facsimile = getResponsableFacsimile(visita?.establecimiento?.organismo?.id);
        // El Entrevistado solo se autocompleta para CPE (ver activeEntrevistadoFacsimile).
        const entrevistadoFacsimile =
            getFacsimileOrganismoKey(visita?.establecimiento?.organismo?.id) === 'cpe'
                ? facsimile
                : null;

        // Mientras la visita esta PROGRAMADA/EN_PROCESO y no tiene firma propia todavia, se
        // precarga la firma cargada en el perfil del preventor (con sus datos), permitiendo
        // que la edite o la borre para volver a firmar a mano.
        const canPrefillPreventorSignature =
            isPreventor && PREVENTOR_SIGNATURE_PREFILL_STATES.includes(visita.estado);

        return {
        esFallida: visita.esFallida ?? false,
        idMotivoFallaVisita: visita.idMotivoFallaVisita ?? null,

        totalPersonal: normalizeNumber(visita.totalPersonal),
        administrativos: normalizeNumber(visita.administrativos) || 0,
        personalProduccion: normalizeNumber(visita.personalProduccion) || 0,
        otrosPersonal: normalizeNumber(visita.otrosPersonal) || 0,

        // Serv. Hig y Seguridad / Med. Laboral: en CPE se precarga con la misma responsable
        // que se autocompleta como Firmante y Entrevistada (ver entrevistadoFacsimile arriba).
        seguridadHigiene:
            normalizeBoolean(visita.seguridadHigiene) ?? (entrevistadoFacsimile ? true : null),

        profesional: visita.profesional ?? (entrevistadoFacsimile ? entrevistadoFacsimile.nombre : ''),
        matricula: visita.matricula ?? (entrevistadoFacsimile ? entrevistadoFacsimile.matricula : ''),
        matriculaNoInforma: visita.matriculaNoInforma ?? false,
        emailHys: visita.emailHys ?? '',
        emailHysNoInforma: visita.emailHysNoInforma ?? false,

        descripcionEstablecimiento: visita.descripcionEstablecimiento ?? '',

        idTipoVisita: visita.idTipoVisita ?? null,
        idSubTipoVisita: visita.idSubTipoVisita ?? null,

        observacion: visita.observacion?.trim()
            ? visita.observacion
            : visita.observacionFalla?.trim()
                ? visita.observacionFalla
                : getPrefilledObservationLabel(visita),
        firmaPreventor:
            visita.firmaPreventor ??
            (canPrefillPreventorSignature ? firmaUsuarioData?.firmaUrl ?? '' : ''),
        firmaResponsable: visita.firmaResponsable ?? '',

        aclaracionPreventor:
            visita.aclaracionPreventor ??
            (canPrefillPreventorSignature
                ? `${currentUser?.nombre ?? ''} ${currentUser?.apellido ?? ''}`.trim()
                : ''),
        dniPreventor:
            visita.dniPreventor ??
            (canPrefillPreventorSignature ? getDniFromCuil(currentUser?.cuil) : ''),
        cargoPreventor:
            visita.cargoPreventor ?? (canPrefillPreventorSignature ? 'Preventor' : ''),

        aclaracionResponsable:
            visita.aclaracionResponsable ?? (facsimile ? facsimile.nombre : ''),
        dniResponsable: visita.dniResponsable ?? (facsimile ? facsimile.dni : ''),
        cargoResponsable: visita.cargoResponsable ?? (facsimile ? facsimile.cargo : ''),

        nombreEntrevistado:
            visita.nombreEntrevistado ??
            visita.entrevistado?.nombre ??
            (entrevistadoFacsimile ? entrevistadoFacsimile.nombre : ''),
        dniEntrevistado:
            visita.dniEntrevistado ??
            visita.entrevistado?.dni ??
            (entrevistadoFacsimile ? entrevistadoFacsimile.dni : ''),
        caracterEntrevistado:
            visita.caracterEntrevistado ??
            visita.entrevistado?.caracter ??
            (entrevistadoFacsimile ? entrevistadoFacsimile.cargo : ''),
        telefonoEntrevistado:
            visita.telefonoEntrevistado ??
            visita.entrevistado?.telefono ??
            (entrevistadoFacsimile ? entrevistadoFacsimile.telefono : ''),
        emailEntrevistado:
            visita.emailEntrevistado ??
            visita.entrevistado?.email ??
            (entrevistadoFacsimile ? entrevistadoFacsimile.email : ''),

        estado: visita.estado ?? 2,
        idPresentacion: visita.idPresentacion ?? null
        };
    }, [getPrefilledObservationLabel, isPreventor, currentUser, firmaUsuarioData]);

    const disableInteractionPreventor =
        isPreventor && PREVENTOR_EDITS_STATES.includes(selectedVisita?.estado);

    const disableInteraction: boolean = Boolean(
        isFailedVisitByState || isAdminBO || disableInteractionPreventor
    );

    const [fetchPresentacionesByEstablecimiento, { data: presentaciones }] =
        useLazyGetPresentacionesByEstablecimientoQuery();
    const selectedEstablishmentId =
        selectedVisita?.idEstablecimiento || selectedVisita?.establecimiento?.id;

    // El establecimiento embebido en la visita no siempre trae direccion/localidad/cliente
    // completos, por eso se pide el detalle completo del establecimiento aparte.
    const { data: establecimientoDetalle } = useGetEmpresaQuery(selectedEstablishmentId, {
        skip: !selectedEstablishmentId
    });
    const { enqueueSnackbar } = useSnackbar();
    const [isFormValid, setIsFormValid] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedVisitaDetail, setSelectedVisitaDetail] = useState<Visita | null>(null);
    const [drawerSiniestroAbierto, setDrawerSiniestroAbierto] = useState(false);
    const [datosSiniestro, setDatosSiniestro] = useState<DatosSiniestroApi | null>(null);
    const [cargandoSiniestro, setCargandoSiniestro] = useState(false);
    const [errorSiniestro, setErrorSiniestro] = useState<string | null>(null);
    const [fetchSiniestro] = useLazyGetVisitaSiniestroQuery();
    const [signatureErrors, setSignatureErrors] = useState({
        firmaPreventor: false,
        firmaResponsable: false
    });

    const loadingAdditionalDocs = loadingVerosimilitud;
    const additionalDocuments = selectedVisita?.documentacionAdicional || [];

    const idEstablecimiento = useMemo(
        () => location.state?.selectedVisita?.establecimiento?.id || null,
        [location.state?.selectedVisita?.establecimiento?.id]
    );
    const handleOpenDrawer = () => {
        setSelectedVisitaDetail(selectedVisita);
        setOpenDrawer(true);
    };

    const abrirDrawerSiniestro = useCallback(async () => {
        setDrawerSiniestroAbierto(true);
        if (datosSiniestro) return;
        setCargandoSiniestro(true);
        setErrorSiniestro(null);
        try {
            const result = await fetchSiniestro({ idVisita: idVisita }).unwrap();
            setDatosSiniestro(result.data);
        } catch {
            setErrorSiniestro('No se pudieron cargar los datos del siniestro.');
        } finally {
            setCargandoSiniestro(false);
        }
    }, [datosSiniestro, fetchSiniestro, idVisita]);

    const cerrarDrawerSiniestro = useCallback(() => {
        setDrawerSiniestroAbierto(false);
    }, []);

    useEffect(() => {
        const siniestroId = selectedVisita?.siniestro?.id;
        if (!siniestroId || datosSiniestro) return;
        setCargandoSiniestro(true);
        setErrorSiniestro(null);
        fetchSiniestro({ idVisita: idVisita })
            .unwrap()
            .then((result) => setDatosSiniestro(result.data))
            .catch(() => setErrorSiniestro('No se pudieron cargar los datos del siniestro.'))
            .finally(() => setCargandoSiniestro(false));
    }, [selectedVisita?.siniestro?.id, datosSiniestro, fetchSiniestro, idVisita]);

    const [updatePresentationState] = useUpdatePresentationStateMutation();

    const [loadingPresentaciones, setLoadingPresentaciones] = useState(false);
    const [patchEjecutionVisit, { isLoading, isError }] = usePatchEjecutionVisitMutation();
    const isSavingInProgress = isLoading;

    const [openDialogRGRL, setOpenDialogRGRL] = useState(false);
    const handleOpenDialogRGRL = () => setOpenDialogRGRL(true);
    const handleCloseDialogRGRL = () => setOpenDialogRGRL(false);

    const mergeStoredFormData = useCallback((storedFormData: any, visita?: any) => {
        if (!visita) return storedFormData;

        const visitFormData = mapVisitaToFormData(visita);
        const mergedFormData = {
            ...visitFormData,
            ...storedFormData
        };

        // La URL presignada de la firma de perfil (firmaUsuarioData.firmaUrl) vence rapido. Si lo
        // que quedo en el borrador de localStorage para firmaPreventor es esa URL (no una firma
        // dibujada a mano ni la propia ya persistida de la visita), se descarta a favor de la que
        // mapVisitaToFormData acaba de recalcular con la firmaUrl mas fresca; si no, se termina
        // mostrando/enviando una URL ya vencida.
        if (isSignatureUrl(storedFormData?.firmaPreventor)) {
            mergedFormData.firmaPreventor = visitFormData.firmaPreventor;
        }

        INTERVIEWED_FORM_FIELDS.forEach((fieldName) => {
            if (!String(storedFormData?.[fieldName] ?? '').trim()) {
                mergedFormData[fieldName] = (visitFormData as Record<string, any>)[fieldName];
            }
        });

        return mergedFormData;
    }, [mapVisitaToFormData]);

    const getInitialFormData = (selectedVisita?: any) => {
        const saved = getStoredFormData(formDataStorageKey);
        if (saved) {
            return mergeStoredFormData(saved, selectedVisita);
        }

        if (!selectedVisita) {
            return {
                esFallida: false,
                idMotivoFallaVisita: null,

                totalPersonal: null,
                administrativos: null,
                personalProduccion: null,
                otrosPersonal: null,
                seguridadHigiene: null,

                profesional: '',
                matricula: '',
                matriculaNoInforma: false,
                emailHys: '',
                emailHysNoInforma: false,

                descripcionEstablecimiento: '',

                idTipoVisita: null,
                idSubTipoVisita: null,

                observacion: '',

                firmaPreventor: '',
                aclaracionPreventor: '',
                dniPreventor: '',
                cargoPreventor: '',

                firmaResponsable: '',
                aclaracionResponsable: '',
                dniResponsable: '',
                cargoResponsable: '',

                nombreEntrevistado: '',
                dniEntrevistado: '',
                caracterEntrevistado: '',
                telefonoEntrevistado: '',
                emailEntrevistado: '',

                estado: 2,
                idPresentacion: null
            };
        }

        return mapVisitaToFormData(selectedVisita);
    };

    const [formData, setFormData] = useState(() => getInitialFormData(selectedVisita));

    const { data: tiposVisitas, isLoading: loadingTiposVisitas } = useGetTiposVisitasQuery(null);
    const { data: motivosFalla, isLoading: loadingMotivosFalla } = useGetMotivosFallaVisitaQuery();
    const isVerosimilitudDraft = selectedVisita?.verosimilitud?.id;

    const tiposVisitasOptions = (tiposVisitas?.data?.tipos || []).map((tipo: any) => ({
        nombre: tipo.descripcion,
        id: tipo.id,
        value: tipo.id
    }));

    const getTipoActividad = (idTipoVisita: number): TipoActividad | undefined => {
        if (!tiposVisitas?.data) return undefined;
        return tiposVisitas.data.find((t: any) => t.id === idTipoVisita);
    };

    const getExecutionVisitTypeIds = () => {
        const activityIds = Array.isArray(selectedVisita?.actividades)
            ? selectedVisita.actividades
                  .map((actividad: any) => actividad?.tipoVisita?.id)
                  .filter((id: unknown): id is number => typeof id === 'number' && id > 0)
            : [];

        if (activityIds.length > 0) {
            return Array.from(new Set(activityIds));
        }

        if (typeof selectedVisita?.idTipoVisita === 'number' && selectedVisita.idTipoVisita > 0) {
            return [selectedVisita.idTipoVisita];
        }

        return [];
    };

    const subTypesVisitasOptions = (tiposVisitas?.data?.subtipos || []).map((tipo: any) => ({
        nombre: tipo.descripcion,
        id: tipo.id,
        value: tipo.id
    }));

    const motivosFallaOptions = (motivosFalla?.data || []).map((motivo: any) => ({
        id: motivo.id,
        codigo: motivo.codigo,
        descripcion: motivo.descripcion
    }));

    // SMP-179: el destino de una visita fallida (cierre definitivo o vuelta a Pendiente para
    // replanificar) depende unicamente del motivo seleccionado, sin intervencion manual.
    const motivoFallaDestino = getMotivoFallaDestino(formData.idMotivoFallaVisita);

    useEffect(() => {
        const isValidWhenFallida =
            formData.esFallida &&
            formData.observacion.trim() !== '' &&
            formData.firmaPreventor !== '' &&
            formData.idMotivoFallaVisita !== null &&
            formData.idMotivoFallaVisita !== undefined;

        const isValidWhenNotFallida =
            !formData.esFallida &&
            formData.totalPersonal !== null &&
            formData.administrativos !== null &&
            formData.personalProduccion !== null &&
            formData.seguridadHigiene !== null &&
            (selectedVisita.nombreTipoVisita !== 'GENERICA' || formData.idSubTipoVisita !== null) &&
            formData.observacion.trim() !== '';

        const isRgrlValid =
            selectedVisita?.nombreTipoVisita !== 'RGRL' ||
            rgrlPresentaciones.some((p) => [2, 3, 4, 5].includes(p.idEstado));

        const isVerosimilitudValid =
            selectedVisita?.nombreTipoVisita !== 'VEROSIMILITUD' || isVerosimilitudDraft;

        if (formData.esFallida) {
            setIsFormValid(isValidWhenFallida);
        } else {
            setIsFormValid(
                isValidWhenNotFallida && isRgrlValid && isVerosimilitudValid
            );
        }
    }, [formData, selectedVisita, rgrlPresentaciones, isVerosimilitudDraft]);

    const initPreventorSignaturePad = () => {
        if (!canvasPreventorRef.current) return;

        signaturePadPreventorRef.current?.off();
        signaturePadPreventorRef.current = new SignaturePad(canvasPreventorRef.current);

        signaturePadPreventorRef.current.addEventListener('endStroke', saveSignaturePreventor);

        resizeCanvas(canvasPreventorRef, signaturePadPreventorRef, formData.firmaPreventor);
    };

    const initResponsableSignaturePad = () => {
        if (!canvasResponsableRef.current) return;

        signaturePadResponsableRef.current?.off();
        signaturePadResponsableRef.current = new SignaturePad(canvasResponsableRef.current);

        signaturePadResponsableRef.current.addEventListener('endStroke', saveSignatureResponsable);

        resizeCanvas(canvasResponsableRef, signaturePadResponsableRef, formData.firmaResponsable);
    };

    const canvasPreventorRef = useRef<HTMLCanvasElement>(null);
    const canvasResponsableRef = useRef<HTMLCanvasElement>(null);
    const signaturePadPreventorRef = useRef<SignaturePad | null>(null);
    const signaturePadResponsableRef = useRef<SignaturePad | null>(null);

    useEffect(() => {
        if (formData.seguridadHigiene === false) {
            setFormData((prev: typeof formData) => ({
                ...prev,
                profesional: '',
                matricula: '',
                matriculaNoInforma: false,
                emailHys: '',
                emailHysNoInforma: false
            }));
        }
    }, [formData.seguridadHigiene]);

    useEffect(() => {
        if (!selectedVisita || isFormInitialized) return;

        const saved = getStoredFormData(formDataStorageKey);

        if (saved) {
            setFormData(mergeStoredFormData(saved, selectedVisita));
            setIsFormInitialized(true);
            return;
        }

        setFormData(mapVisitaToFormData(selectedVisita));
        setIsFormInitialized(true);
    }, [
        selectedVisita,
        isFormInitialized,
        mapVisitaToFormData,
        mergeStoredFormData,
        formDataStorageKey
    ]);

    // La firma del perfil (useGetUserFirmaQuery) es asincronica y casi siempre llega despues de
    // que el formulario ya se inicializo, asi que mapVisitaToFormData la pierde en ese primer
    // armado. Una vez que llega, si todavia no hay nada cargado (ni dibujado a mano ni una firma
    // propia de la visita), se completa aca sin pisar lo que el preventor ya haya hecho.
    useEffect(() => {
        if (!isFormInitialized || !isPreventor || !firmaUsuarioData?.firmaUrl) return;
        if (!PREVENTOR_SIGNATURE_PREFILL_STATES.includes(selectedVisita?.estado)) return;
        if (selectedVisita?.firmaPreventor) return;

        setFormData((prev: typeof formData) => {
            if (prev.firmaPreventor) return prev;

            return {
                ...prev,
                firmaPreventor: firmaUsuarioData.firmaUrl,
                aclaracionPreventor:
                    prev.aclaracionPreventor ||
                    `${currentUser?.nombre ?? ''} ${currentUser?.apellido ?? ''}`.trim(),
                dniPreventor: prev.dniPreventor || getDniFromCuil(currentUser?.cuil),
                cargoPreventor: prev.cargoPreventor || 'Preventor'
            };
        });
    }, [
        isFormInitialized,
        isPreventor,
        firmaUsuarioData,
        selectedVisita?.estado,
        selectedVisita?.firmaPreventor,
        currentUser
    ]);

    // Si el organismo tiene firma facsimil (CPE, Tribunal), garantiza los datos de esa firma
    // (incluida la imagen que se envia como firmaResponsable, igual que la firma dibujada del
    // preventor), aunque el formData se haya inicializado antes de saber que organismo era, o un
    // borrador viejo en localStorage haya pisado estos campos con valores vacios de otra visita.
    // Los datos de la persona entrevistada/del establecimiento y del bloque Serv. Hig y Seguridad
    // / Med. Laboral solo se garantizan asi para CPE (activeEntrevistadoFacsimile es null para
    // Tribunal): ese organismo completa esos bloques a mano.
    useEffect(() => {
        if (!activeResponsableFacsimile) return;

        const facsimile = activeResponsableFacsimile;
        const entrevistadoFacsimile = activeEntrevistadoFacsimile;

        setFormData((prev: typeof formData) => {
            const needsNombre = !prev.aclaracionResponsable?.trim();
            const needsDni = !prev.dniResponsable?.trim();
            const needsCargo = !prev.cargoResponsable?.trim();

            const needsNombreEntrevistado =
                !!entrevistadoFacsimile && !prev.nombreEntrevistado?.trim();
            const needsDniEntrevistado =
                !!entrevistadoFacsimile && !prev.dniEntrevistado?.trim();
            const needsCaracterEntrevistado =
                !!entrevistadoFacsimile && !prev.caracterEntrevistado?.trim();
            const needsTelefonoEntrevistado =
                !!entrevistadoFacsimile && !prev.telefonoEntrevistado?.trim();
            const needsEmailEntrevistado =
                !!entrevistadoFacsimile && !prev.emailEntrevistado?.trim();

            const needsSeguridadHigiene = !!entrevistadoFacsimile && prev.seguridadHigiene == null;
            const needsProfesional = !!entrevistadoFacsimile && !prev.profesional?.trim();
            const needsMatricula = !!entrevistadoFacsimile && !prev.matricula?.trim();

            if (
                !needsNombre &&
                !needsDni &&
                !needsCargo &&
                !needsNombreEntrevistado &&
                !needsDniEntrevistado &&
                !needsCaracterEntrevistado &&
                !needsTelefonoEntrevistado &&
                !needsEmailEntrevistado &&
                !needsSeguridadHigiene &&
                !needsProfesional &&
                !needsMatricula
            ) {
                return prev;
            }

            return {
                ...prev,
                aclaracionResponsable: needsNombre ? facsimile.nombre : prev.aclaracionResponsable,
                dniResponsable: needsDni ? facsimile.dni : prev.dniResponsable,
                cargoResponsable: needsCargo ? facsimile.cargo : prev.cargoResponsable,
                nombreEntrevistado: needsNombreEntrevistado
                    ? (entrevistadoFacsimile as typeof facsimile).nombre
                    : prev.nombreEntrevistado,
                dniEntrevistado: needsDniEntrevistado
                    ? (entrevistadoFacsimile as typeof facsimile).dni
                    : prev.dniEntrevistado,
                caracterEntrevistado: needsCaracterEntrevistado
                    ? (entrevistadoFacsimile as typeof facsimile).cargo
                    : prev.caracterEntrevistado,
                telefonoEntrevistado: needsTelefonoEntrevistado
                    ? (entrevistadoFacsimile as typeof facsimile).telefono
                    : prev.telefonoEntrevistado,
                emailEntrevistado: needsEmailEntrevistado
                    ? (entrevistadoFacsimile as typeof facsimile).email
                    : prev.emailEntrevistado,
                seguridadHigiene: needsSeguridadHigiene ? true : prev.seguridadHigiene,
                profesional: needsProfesional
                    ? (entrevistadoFacsimile as typeof facsimile).nombre
                    : prev.profesional,
                matricula: needsMatricula
                    ? (entrevistadoFacsimile as typeof facsimile).matricula
                    : prev.matricula
            };
        });
    }, [activeResponsableFacsimile, activeEntrevistadoFacsimile]);

    // La imagen de la firma facsimil se obtiene de forma asincronica (se lee el archivo
    // estatico), por eso se resuelve en un efecto aparte del resto de los campos.
    // En una fallida no aplica: el responsable firma a mano o no firma (SMP-416).
    useEffect(() => {
        if (!facsimileOrganismoKey || formData.esFallida || formData.firmaResponsable?.trim()) return;

        let cancelled = false;

        buildResponsableFacsimileImage(facsimileOrganismoKey).then((firmaResponsable) => {
            if (cancelled) return;

            setFormData((prev: typeof formData) =>
                prev.esFallida || prev.firmaResponsable?.trim() ? prev : { ...prev, firmaResponsable }
            );
        });

        return () => {
            cancelled = true;
        };
    }, [facsimileOrganismoKey, formData.esFallida, formData.firmaResponsable]);

    useEffect(() => {
        if (!isFormInitialized) return;

        localStorage.setItem(formDataStorageKey, JSON.stringify(formData));
    }, [formData, isFormInitialized, formDataStorageKey]);

    // Restaura en el canvas una firma dibujada previamente (guardada en formData como base64
    // sin el prefijo "data:") cuando todavia no fue enviada al backend. Sin esto, al remontar
    // la pantalla de ejecucion (p.ej. al volver de "Seguimiento Verosimilitud", "Visita Tecnica"
    // o "Formulario SRT") el SignaturePad se crea vacio y la firma parece perdida, aunque el
    // valor siga presente en formData/localStorage.
    const drawStoredSignature = (
        signaturePadRef: React.RefObject<SignaturePad | null>,
        storedSignature?: string
    ) => {
        if (!storedSignature || /^https?:\/\//.test(storedSignature)) return;

        const dataUrl = storedSignature.startsWith('data:')
            ? storedSignature
            : `data:image/png;base64,${storedSignature}`;

        signaturePadRef.current?.fromDataURL(dataUrl)?.catch(() => {});
    };

    const resizeCanvas = (
        canvasRef: React.RefObject<HTMLCanvasElement>,
        signaturePadRef: React.RefObject<SignaturePad | null>,
        storedSignature?: string
    ) => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const ratio = window.devicePixelRatio || 1;
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = 150 * ratio;
            const ctx = canvas.getContext('2d');
            ctx?.scale(ratio, ratio);
            signaturePadRef.current?.clear();
            drawStoredSignature(signaturePadRef, storedSignature);
        }
    };

    const formDataRef = useRef(formData);
    formDataRef.current = formData;

    useEffect(() => {
        if (canvasPreventorRef.current) {
            signaturePadPreventorRef.current = new SignaturePad(canvasPreventorRef.current);
            signaturePadPreventorRef.current.addEventListener('endStroke', saveSignaturePreventor);
            resizeCanvas(canvasPreventorRef, signaturePadPreventorRef, formData.firmaPreventor);
        }
        if (canvasResponsableRef.current) {
            signaturePadResponsableRef.current = new SignaturePad(canvasResponsableRef.current);
            signaturePadResponsableRef.current.addEventListener(
                'endStroke',
                saveSignatureResponsable
            );
            resizeCanvas(canvasResponsableRef, signaturePadResponsableRef, formData.firmaResponsable);
        }

        // Al cambiar el tamaño, resizeCanvas limpia el canvas: se vuelve a dibujar la firma
        // vigente (formDataRef, no el formData del primer render). Se registra y se quita la
        // misma función para no acumular listeners cada vez que se remonta la pantalla.
        const handleResize = () => {
            resizeCanvas(
                canvasPreventorRef,
                signaturePadPreventorRef,
                formDataRef.current?.firmaPreventor
            );
            resizeCanvas(
                canvasResponsableRef,
                signaturePadResponsableRef,
                formDataRef.current?.firmaResponsable
            );
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const saveSignaturePreventor = () => {
        if (signaturePadPreventorRef.current && !signaturePadPreventorRef.current.isEmpty()) {
            const signatureEncode = signaturePadPreventorRef.current.toDataURL('image/png');
            const bufferBase64 = signatureEncode.replace(/^data:image\/png;base64,/, '');
            setFormData((prev: any) => ({
                ...prev,
                firmaPreventor: bufferBase64
            }));
        }
    };

    const saveSignatureResponsable = () => {
        if (signaturePadResponsableRef.current && !signaturePadResponsableRef.current.isEmpty()) {
            const signatureEncode = signaturePadResponsableRef.current.toDataURL('image/png');
            const bufferBase64 = signatureEncode.replace(/^data:image\/png;base64,/, '');
            setFormData((prev: any) => ({
                ...prev,
                firmaResponsable: bufferBase64
            }));
        }
    };

    const handleClearSignature = (type: 'preventor' | 'responsable') => {
        if (type === 'preventor' && signaturePadPreventorRef.current) {
            signaturePadPreventorRef.current.clear();
            setFormData((prev: typeof formData) => ({ ...prev, firmaPreventor: '' }));
        } else if (type === 'responsable' && signaturePadResponsableRef.current) {
            signaturePadResponsableRef.current.clear();
            setFormData((prev: typeof formData) => ({ ...prev, firmaResponsable: '' }));
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setFormData((prev: typeof formData) => ({
            ...prev,
            [name]: name === 'subtipoVisita' ? Number(value) : value
        }));
    };

    const handleFalloVisitaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;

        if (checked) {
            signaturePadResponsableRef.current?.clear();
        }

        setFormData((prev: any) => ({
            ...prev,
            esFallida: checked,
            idMotivoFallaVisita: checked ? prev.idMotivoFallaVisita : null,

            totalPersonal: checked ? null : prev.totalPersonal,
            administrativos: checked ? null : prev.administrativos,
            personalProduccion: checked ? null : prev.personalProduccion,
            otrosPersonal: checked ? null : prev.otrosPersonal,

            matriculaNoInforma: checked ? false : prev.matriculaNoInforma,
            emailHys: checked ? '' : prev.emailHys,
            emailHysNoInforma: checked ? false : prev.emailHysNoInforma,

            seguridadHigiene: checked
                ? null
                : activeEntrevistadoFacsimile
                    ? true
                    : prev.seguridadHigiene,
            profesional: checked
                ? ''
                : activeEntrevistadoFacsimile
                    ? activeEntrevistadoFacsimile.nombre
                    : prev.profesional,
            matricula: checked
                ? ''
                : activeEntrevistadoFacsimile
                    ? activeEntrevistadoFacsimile.matricula
                    : prev.matricula,

            idSubTipoVisita: checked ? null : prev.idSubTipoVisita,

            // La fallida arranca sin el texto precargado por tipo de visita (es de una visita
            // realizada): la observación es obligatoria y el preventor tiene que escribir la de la
            // falla. Lo que ya escribió se conserva. Al destildar, si quedó vacía, vuelve el
            // precargado.
            observacion: (() => {
                const precargada = getPrefilledObservationLabel(selectedVisita);
                if (checked) {
                    return precargada && prev.observacion === precargada ? '' : prev.observacion;
                }
                return prev.observacion?.trim() ? prev.observacion : precargada;
            })(),

            // La firma del preventor sigue siendo obligatoria para confirmar la visita fallida
            // (ver isValidWhenFallida): a diferencia de la firma del responsable, no se limpia al
            // tildar el checkbox.
            firmaPreventor: prev.firmaPreventor,
            firmaResponsable: '',

            aclaracionPreventor: prev.aclaracionPreventor,
            dniPreventor: prev.dniPreventor,
            cargoPreventor: prev.cargoPreventor,

            // En la fallida, los datos del responsable y de quien recibió al preventor siguen
            // habilitados y son opcionales (SMP-416): se conserva lo cargado o precargado.
            // La firma del responsable sí se limpia: el facsímil no aplica a una fallida.
            aclaracionResponsable:
                checked || !activeResponsableFacsimile
                    ? prev.aclaracionResponsable
                    : activeResponsableFacsimile.nombre,
            dniResponsable:
                checked || !activeResponsableFacsimile
                    ? prev.dniResponsable
                    : activeResponsableFacsimile.dni,
            cargoResponsable:
                checked || !activeResponsableFacsimile
                    ? prev.cargoResponsable
                    : activeResponsableFacsimile.cargo,

            nombreEntrevistado:
                checked || !activeEntrevistadoFacsimile
                    ? prev.nombreEntrevistado
                    : activeEntrevistadoFacsimile.nombre,
            dniEntrevistado:
                checked || !activeEntrevistadoFacsimile
                    ? prev.dniEntrevistado
                    : activeEntrevistadoFacsimile.dni,
            caracterEntrevistado:
                checked || !activeEntrevistadoFacsimile
                    ? prev.caracterEntrevistado
                    : activeEntrevistadoFacsimile.cargo,
            telefonoEntrevistado:
                checked || !activeEntrevistadoFacsimile
                    ? prev.telefonoEntrevistado
                    : activeEntrevistadoFacsimile.telefono,
            emailEntrevistado:
                checked || !activeEntrevistadoFacsimile
                    ? prev.emailEntrevistado
                    : activeEntrevistadoFacsimile.email,

            estado: checked ? 3 : 2
        }));
    };

    const confirmSubmit = () => {
        const alertTitle = formData.esFallida
            ? t('execution.failedVisitConfirmTitle')
            : t('execution.confirmTitle');

        const alertMessage = !formData.esFallida
            ? t('execution.confirmMessage')
            : motivoFallaDestino === 'replanificacion'
                ? t('execution.failedVisitReplanificacionConfirmMessage')
                : t('execution.failedVisitCierreConfirmMessage');

        dispatch(
            showAlert({
                icon: 'info',
                title: alertTitle,
                message: alertMessage,
                confirmText: t('execution.confirmAction'),
                cancelText: t('buttons.cancel'),
                itemData: true,
                confirmAction: handleSubmit
            })
        );
    };


    // La dotacion total ya no se carga a mano: se recalcula cada vez que cambia un componente.
    useEffect(() => {
        const totalPersonal = calcularTotalPersonal(
            formData.administrativos,
            formData.personalProduccion,
            formData.otrosPersonal
        );
        setFormData((prev: typeof formData) =>
            prev.totalPersonal === totalPersonal ? prev : { ...prev, totalPersonal }
        );
    }, [formData.administrativos, formData.personalProduccion, formData.otrosPersonal]);

    // "No Informa" y el valor del campo se excluyen: al activarlo se descarta lo ingresado.
    const handleNoInformaChange =
        (campo: 'matricula' | 'emailHys') => (event: React.ChangeEvent<HTMLInputElement>) => {
            const { checked } = event.target;
            const flag = campo === 'matricula' ? 'matriculaNoInforma' : 'emailHysNoInforma';

            setFormData((prev: typeof formData) => ({
                ...prev,
                [flag]: checked,
                [campo]: checked ? '' : prev[campo]
            }));
        };

    const buildPatchPayload = (options?: { skipValidation?: boolean }) => {
        const isBorrador = Boolean(options?.skipValidation);

        const payload: any = {
            idVisita: selectedVisita.id,
            esFallida: formData.esFallida,
            observacion: formData.observacion ?? '',
            estado: isBorrador
                ? selectedVisita.idEstado
                : formData.esFallida
                    ? motivoFallaDestino === 'replanificacion'
                        ? VisitaEstado.PENDIENTE
                        : VisitaEstado.FALLIDA
                    : VisitaEstado.REALIZADA,
            idsTipoVisita: getExecutionVisitTypeIds(),
            fechaLimite: selectedVisita?.fechaLimite ?? null
        };

        if (isBorrador) {
            payload.esBorrador = true;
        }

        if (formData.esFallida) {
            payload.idMotivoFallaVisita = formData.idMotivoFallaVisita;
            payload.observacionFalla = formData.observacion ?? '';
        }

        payload.firmaPreventor = formData.firmaPreventor || selectedVisita.firmaPreventor;

        // En una visita fallida la firma del responsable es opcional y vale solo la de esta
        // falla: no se reusa una firma anterior de la visita.
        payload.firmaResponsable = formData.esFallida
            ? formData.firmaResponsable || ''
            : formData.firmaResponsable || selectedVisita.firmaResponsable;
        payload.aclaracionPreventor =
            formData.aclaracionPreventor || selectedVisita.aclaracionPreventor || '';
        payload.dniPreventor = formData.dniPreventor || selectedVisita.dniPreventor || '';
        payload.cargoPreventor = formData.cargoPreventor || selectedVisita.cargoPreventor || '';

        payload.aclaracionResponsable = formData.aclaracionResponsable;
        payload.dniResponsable = formData.dniResponsable;
        payload.cargoResponsable = formData.cargoResponsable;

        if (formData.totalPersonal !== null) payload.totalPersonal = Number(formData.totalPersonal);

        if (formData.administrativos !== null)
            payload.administrativos = Number(formData.administrativos);

        if (formData.personalProduccion !== null)
            payload.personalProduccion = Number(formData.personalProduccion);

        if (formData.otrosPersonal !== null && formData.otrosPersonal !== '')
            payload.otrosPersonal = Number(formData.otrosPersonal);

        if (formData.seguridadHigiene !== null)
            payload.seguridadHigiene = formData.seguridadHigiene;

        if (formData.profesional?.trim()) payload.profesional = formData.profesional;

        if (formData.matricula?.trim() && !formData.matriculaNoInforma)
            payload.matricula = formData.matricula;

        if (formData.emailHys?.trim() && !formData.emailHysNoInforma)
            payload.emailHys = formData.emailHys.trim();

        if (formData.seguridadHigiene) {
            payload.matriculaNoInforma = Boolean(formData.matriculaNoInforma);
            payload.emailHysNoInforma = Boolean(formData.emailHysNoInforma);
        }

        // Se manda siempre, vacia incluida, para que borrar el texto tambien se persista.
        payload.descripcionEstablecimiento = formData.descripcionEstablecimiento ?? '';

        if (formData.idSubTipoVisita !== null) payload.idSubTipoVisita = formData.idSubTipoVisita;

        payload.nombreEntrevistado = formData.nombreEntrevistado ?? '';
        payload.dniEntrevistado = formData.dniEntrevistado ?? '';
        payload.caracterEntrevistado = formData.caracterEntrevistado ?? '';
        payload.telefonoEntrevistado = formData.telefonoEntrevistado ?? '';
        payload.emailEntrevistado = formData.emailEntrevistado ?? '';

        return payload;
    };

    // Si la firma del preventor quedo precargada como URL (ver el efecto que usa
    // firmaUsuarioData.firmaUrl) y no fue re-dibujada a mano, se envia esa URL tal cual. El
    // backend espera base64, igual que la firma dibujada, asi que se convierte aca antes de
    // mandarla al guardar en progreso o al ejecutar.
    const resolveFirmaPreventorForPayload = async (payload: any) => {
        if (!isSignatureUrl(payload.firmaPreventor)) return payload;

        return { ...payload, firmaPreventor: await signatureUrlToBase64(payload.firmaPreventor) };
    };

    const handleSaveInProgress = async () => {
        try {
            const payload = await resolveFirmaPreventorForPayload(
                buildPatchPayload({ skipValidation: true })
            );

            await patchEjecutionVisit(payload).unwrap();

            enqueueSnackbar('Visita guardada en progreso', { variant: 'success' });
            refetchVisita();
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo guardar en progreso',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleSubmit = async () => {
        try {
            const payload = await resolveFirmaPreventorForPayload(buildPatchPayload());

            await patchEjecutionVisit(payload).unwrap();
            localStorage.removeItem(formDataStorageKey);

            enqueueSnackbar(t('execution.successSnackbar'), { variant: 'success' });
            navigate(
                location.state?.fromVisitasRoam ? '/backoffice/visitas-roam' : '/backoffice/visitas'
            );
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo ejecutar la visita',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const handleFetchPresentaciones = useCallback(async () => {
        if (!idEstablecimiento) return;

        try {
            setLoadingPresentaciones(true);
            const response = await fetchPresentacionesByEstablecimiento({
                idEstablecimiento,
                take: 100,
                skip: 0
            }).unwrap();
            const rgrl = response?.data?.data
                .filter((p: any) => p.tipo?.nombre === 'RGRL')
                // Mismo criterio que Presentacion.getLastRGRL() (Back-APIVisitas, domain/presentacion.mts):
                // la API no garantiza orden cronológico, hay que resolver el más reciente explícitamente.
                .sort(
                    (a: any, b: any) =>
                        new Date(b.fechaPresentacion).getTime() - new Date(a.fechaPresentacion).getTime()
                );
            setRgrlPresentaciones(rgrl?.slice(0, 1) ?? []);
        } catch (error) {
        } finally {
            setLoadingPresentaciones(false);
        }
    }, [fetchPresentacionesByEstablecimiento, idEstablecimiento]);

    useEffect(() => {
        handleFetchPresentaciones();
    }, [handleFetchPresentaciones, location.key]);

    const handleCardClickRGRL = (idTipoPresentacion: number, titulo: string) => {
        dispatch(setEstablishmentId(idEstablecimiento));
        localStorage.setItem(formDataStorageKey, JSON.stringify(formData));

        navigate('./crear-rgrl', {
            state: {
                tipoId: idTipoPresentacion,
                tipoTitulo: titulo,
                formDataState: formData
            }
        });
    };

    const handleOpenVerosimilitud = ({ mode, idVerosimilitud }: OpenVerosimilitudParams) => {
        const firstPresentation = rgrlPresentaciones[0];
        if (!firstPresentation) return;

        dispatch(setEstablishmentId(idEstablecimiento));

        navigate('./verosimilitud', {
            state: {
                mode,
                idVerosimilitud,
                selectedPresentationId: firstPresentation.id,
                selectedEstablishmentId,
                tipoId: firstPresentation.tipo?.id,
                idVisita: selectedVisita?.id,
                idPreventor: selectedVisita?.idPreventor,
                observacion: formData.observacion
            }
        });
    };

    const shouldShowVerosimilitudRow =
        selectedVisita?.idEstado === VisitaEstado.PENDIENTE &&
        Array.isArray(selectedVisita.verosimilitudRespuestas) &&
        selectedVisita.verosimilitudRespuestas.length === 0;

    const isVerosimilitudPendienteSinRespuestas =
        selectedVisita?.idEstado === VisitaEstado.PENDIENTE &&
        Array.isArray(selectedVisita?.verosimilitudRespuestas) &&
        selectedVisita.verosimilitudRespuestas.length === 0;

    const confirmCancelPresentation = (idPresentation: number) => {
        dispatch(
            showAlert({
                icon: 'error',
                title: t('alerts.cancelPresentation.title'),
                message: t('alerts.cancelPresentation.message'),
                confirmText: t('alerts.cancelPresentation.confirmText'),
                cancelText: t('alerts.cancelPresentation.cancelText'),
                itemData: true,
                confirmAction: () => handleCancelPresentation(idPresentation)
            })
        );
    };

    const handleCancelPresentation = async (idPresentation: number) => {
        try {
            await updatePresentationState({
                presentationId: idPresentation,
                body: { idEstado: 6 }
            }).unwrap();

            enqueueSnackbar(t('alerts.cancelPresentation.success'), { variant: 'success' });
            handleFetchPresentaciones();
        } catch (error: any) {
            dispatch(
                showAlert({
                    title: 'No se pudo cancelar la presentación',
                    message: error?.data?.message || ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    const allPresentationsCancelled =
        rgrlPresentaciones.length > 0 && rgrlPresentaciones.every((p: any) => p.idEstado === 6);

    const hasRgrlPresentations = () => {
        return rgrlPresentaciones.length > 0;
    };

    const handleEditRGRL = (presentacion: any) => {
        localStorage.setItem(formDataStorageKey, JSON.stringify(formData));
        if (!presentacion?.id) return;
        navigate('./editar', {
            state: {
                selectedPresentationId: presentacion.id,
                selectedEstablishmentId: presentacion.establecimiento.id,
                tipoId: presentacion.tipo.id,
                tipoTitulo: presentacion.tipo.nombre,
                formDataState: formData
            }
        });
    };

    return {
        formData,
        setFormData,
        isFormValid,
        handleChange,
        handleFalloVisitaChange,
        handleNoInformaChange,
        confirmSubmit,
        handleSubmit,
        handleSaveInProgress,
        handleClearSignature,
        openDrawer,
        setOpenDrawer,
        handleOpenDrawer,
        selectedVisita,
        selectedVisitaDetail,
        canvasPreventorRef,
        canvasResponsableRef,
        tiposVisitasOptions,
        subTypesVisitasOptions,
        loadingTiposVisitas,
        motivosFallaOptions,
        loadingMotivosFalla,
        signatureErrors,
        rgrlPresentaciones,
        openDialogRGRL,
        handleOpenDialogRGRL,
        handleCloseDialogRGRL,
        handleCardClickRGRL,
        handleOpenVerosimilitud,
        loadingPresentaciones,
        shouldShowVerosimilitudRow,
        isVerosimilitudDraft,
        confirmCancelPresentation,
        allPresentationsCancelled,
        handleEditRGRL,
        isVerosimilitudPendienteSinRespuestas,
        hasRgrlPresentations,
        loadingVerosimilitud,
        loadingAdditionalDocs,
        additionalDocuments,
        getTipoActividad,
        refetchVisita,
        disableInteraction,
        isSavingInProgress,
        isFailedVisitByState,
        initPreventorSignaturePad,
        initResponsableSignaturePad,
        drawerSiniestroAbierto,
        abrirDrawerSiniestro,
        cerrarDrawerSiniestro,
        datosSiniestro,
        cargandoSiniestro,
        errorSiniestro,
        readOnlySrt,
        esVisitaInvestigacion,
        esSeguimientoVerosimilitud,
        isCPEOrganismo: hasFacsimileOrganismo,
        facsimileOrganismoKey,
        establecimientoDetalle
    };
};

